/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {KeyboardEventHandler} from 'react';

/**
 * Provides the pre-defined list of observable key-commands which can be used.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
 * @see https://www.w3.org/TR/uievents-key/#named-key-attribute-values
 */
export type Key = (
  'Escape' | 'Tab' | 'Backspace'
);

/**
 * Defines the modifier types that can be used in a combination key command,
 * such as 'ctrl+x', etc
 */
export type Modifiers = {
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
};

/**
 * Defines the function type returned from the `useKeyCommand` hook.
 */
export type KeyCommandHandler = (event: KeyboardEventHandler) => void;

/**
 * Provides a utility which compares the state of the `KeyboardEvent` modifier
 * attributes against the modifiers being observed (if any).
 */
const isValidateModifier = (evt: KeyboardEvent, mod: Modifiers = {}): boolean => (
  evt.shiftKey === !!mod.shift &&
  evt.ctrlKey === !!mod.ctrl &&
  evt.metaKey === !!mod.meta &&
  evt.altKey === !!mod.alt
);

/**
 * The `useKeyCommand` hook allows for creating a callback function that can be
 * passed to components for use with keyboard events, such as the `onKeyDown`,
 * `onKeyUp`, etc. element event attributes etc.
 *
 * Components can use this hook to provide a function to an element for handling
 * key events of interest.
 *
 * ```typescript
 *   import useKeyCommand from '@dls/src/hooks/useKeyCommand';
 *
 *   const SomeComponent = ({onClose}: SomeComponentProps) => {
 *     const onEscape = useKeyCommand('Escape', onClose);
 *     ...
 *     return (
 *       {/* invokes onClose when the user presses the `Escape` key ...}
 *       <div onKeyDown={onEscape} ... />
 *     );
 *   };
 * ```
 *
 * The `useKeyCommand` hook can also be invoked with a specific character key
 * and a modifier key to allow for invoking the given function when a specific
 * multi-key command is entered, such as `ctrl+x`, etc.
 *
 * ```typescript
 *   import useKeyCommand from '@dls/src/hooks/useKeyCommand';
 *
 *   const SomeComponent = ({onClose}: SomeComponentProps) => {
 *     // invoke the 'onClose' function when the user presses 'ctrl+x' ...
 *     const onCommand = useKeyCommand('x', onClose, {ctrl: true});
 *     ...
 *     return (
 *       {/* invoked when 'ctrl+x' is pressed ... }
 *       <div onKeyDown={onCommand} ... />
 *     );
 *   };
 * ```
 *
 * @param   key The `Key` for which the `command` is to be invoked when pressed.
 *          When used for observing a combinatorial key command, such as `ctrl+c',
 *          a string can be used as the `key`, and the `modifiers` argument is
 *          required.
 * @param   command The command which is to be invoked when the `key` command
 *          is pressed.
 * @param   modifiers Optional `Modifiers` that can be included when used in a
 *          combination key command. Note, this parameter is required when the
 *          `key` argument is a string that is not defined within the `Key` type.
 * @returns A function which handles a `KeyboardEvent` that can be passed to a
 *          an applicable HTML DOM keyboard event attribute.
 */
export function useKeyCommand(
  key: string,
  command: Function,
  modifiers: Modifiers
): KeyCommandHandler;

export function useKeyCommand(
  key: Key,
  command?: Function
): KeyCommandHandler;

export function useKeyCommand(key: Key, command?: Function, modifiers?: Modifiers): KeyCommandHandler {
  const onKeyCommand = (event: KeyboardEventHandler<HTMLElement>) => {
    const evt = event as unknown as KeyboardEvent;
    evt.stopPropagation();
    if (evt.key === key && isValidateModifier(evt, modifiers)) {
      command?.();
    }
  };
  return onKeyCommand;
};
export default useKeyCommand;
